import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import FileDiffViewer from './FileDiffViewer';

const GitHubSyncPanel = ({ project, onClose, onSyncComplete }) => {
  const [modifiedFiles, setModifiedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [commitMessage, setCommitMessage] = useState('');
  const [createBranch, setCreateBranch] = useState(false);
  const [branchName, setBranchName] = useState('');
  const [createPR, setCreatePR] = useState(false);
  const [prTitle, setPrTitle] = useState('');
  const [prDescription, setPrDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewingDiffFileId, setViewingDiffFileId] = useState(null);

  useEffect(() => {
    fetchModifiedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const fetchModifiedFiles = async () => {
    try {
      const files = await apiService.getModifiedFiles(project.id);
      setModifiedFiles(files);
      setSelectedFiles(files.map(f => f.id));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSync = async () => {
    if (!commitMessage.trim()) {
      setError('Please enter a commit message');
      return;
    }

    if (createBranch && !branchName.trim()) {
      setError('Please enter a branch name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await apiService.syncToGitHub(project.id, {
        project_id: project.id,
        commit_message: commitMessage,
        files_to_commit: selectedFiles.length === modifiedFiles.length ? null : selectedFiles,
        create_branch: createBranch,
        new_branch_name: branchName || null,
        create_pr: createPR,
        pr_title: prTitle || null,
        pr_description: prDescription || null
      });

      onSyncComplete(result);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Sync to GitHub
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {modifiedFiles.length} file{modifiedFiles.length !== 1 ? 's' : ''} modified
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {/* Modified Files List */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Files to Commit ({selectedFiles.length}/{modifiedFiles.length})
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded-md max-h-48 overflow-auto">
              {modifiedFiles.map(file => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                >
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => toggleFileSelection(file.id)}
                      className="rounded text-blue-600"
                    />
                    <span className="text-sm text-gray-900 dark:text-white font-mono">
                      {file.path}
                    </span>
                  </label>
                  <button
                    onClick={() => setViewingDiffFileId(file.id)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    View Diff
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Commit Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Commit Message *
            </label>
            <textarea
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="Update files via code editor"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows="3"
              required
            />
          </div>

          {/* Branch Options */}
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={createBranch}
                onChange={(e) => setCreateBranch(e.target.checked)}
                className="rounded text-blue-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Create new branch
              </span>
            </label>

            {createBranch && (
              <div>
                <input
                  type="text"
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                  placeholder="feature/my-changes"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            )}

            {createBranch && (
              <div className="space-y-3 pl-6 border-l-2 border-gray-300 dark:border-gray-600">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={createPR}
                    onChange={(e) => setCreatePR(e.target.checked)}
                    className="rounded text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Create Pull Request
                  </span>
                </label>

                {createPR && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={prTitle}
                      onChange={(e) => setPrTitle(e.target.value)}
                      placeholder="PR Title (optional)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <textarea
                      value={prDescription}
                      onChange={(e) => setPrDescription(e.target.value)}
                      placeholder="PR Description (optional)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      rows="2"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSync}
            disabled={loading || selectedFiles.length === 0}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-md hover:from-green-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span>Commit & Push</span>
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Diff Viewer */}
      {viewingDiffFileId && (
        <FileDiffViewer
          fileId={viewingDiffFileId}
          onClose={() => setViewingDiffFileId(null)}
        />
      )}
    </div>
  );
};

export default GitHubSyncPanel;
